import { type PermissionName, useUser } from '@/shared/session';
import { useCallback, useMemo } from 'react';

/**
 * Checks the current user's permissions.
 *
 * A permission is granted when the user has a record for it that is `active` and in the
 * `assigned` state.
 *
 * @returns
 * - `can(permission)` - whether the user has one permission
 * - `canAny(...permissions)` - whether the user has at least one of them
 * - `canAll(...permissions)` - whether the user has every one of them
 *
 * `canAny` and `canAll` return `false` when called with no permissions.
 *
 * @example
 * ```tsx
 * const { can, canAny } = usePermissions();
 *
 * if (can('user.zgri.use-experimental-features')) {
 *   // ...
 * }
 *
 * canAny('user.zgri.use-landlord-features', 'user.zgri.use-ops-features');
 * ```
 */
export function usePermissions() {
  const { isPublic, user } = useUser();

  const permissions = useMemo(() => {
    if (isPublic) {
      return [];
    }

    const userPermissions = user?.userPermissions ?? [];

    return userPermissions;
  }, [isPublic, user?.userPermissions]);

  const can = useCallback(
    (permission: PermissionName) => {
      const permissionRecord = permissions.find((p) => p.permission_name === permission);
      if (!permissionRecord) {
        return false;
      }

      if (!permissionRecord.active) {
        return false;
      }

      return permissionRecord.state === 'assigned';
    },
    [permissions],
  );

  const canAny = useCallback(
    (...checked: PermissionName[]) => {
      if (checked.length === 0) {
        return false;
      }

      return checked.some((permission) => can(permission));
    },
    [can],
  );

  const canAll = useCallback(
    (...checked: PermissionName[]) => {
      if (checked.length === 0) {
        return false;
      }

      return checked.every((permission) => can(permission));
    },
    [can],
  );

  return {
    can,
    canAny,
    canAll,
  };
}
